import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { GalleryVerticalEnd } from "lucide-react";
import authService from "@/services/authService";

export function ResetPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Extract email from URL params on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(emailParam);
    } else {
      // If no email provided, redirect to forgot password
      navigate("/forgot-password");
    }
  }, [location, navigate]);

  // Reset password form validation
  const resetPasswordForm = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("validation.passwordMinLength"))
        .required(t("validation.passwordRequired")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], t("validation.passwordMismatch"))
        .required(t("validation.confirmPasswordRequired")),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      try {
        // Call authService to complete password reset
        await authService.resetPassword({
          email,
          password: values.password,
        });

        // Show success message then redirect to login after a delay
        setSuccess(true);
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.passwordResetError"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex justify-center gap-2 mb-4">
          <div className="flex items-center gap-2 font-medium">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <GalleryVerticalEnd className="h-4 w-4" />
            </div>
            {t("app.title")}
          </div>
        </div>
        <CardTitle className="text-2xl text-center">
          {success
            ? t("auth.passwordResetSuccessful")
            : t("auth.resetYourPassword")}
        </CardTitle>
        <CardDescription className="text-center">
          {success
            ? t("auth.passwordResetMessage")
            : t("auth.newPasswordPrompt")}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error && (
          <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
            {error}
          </div>
        )}

        {success ? (
          <div className="p-3 text-sm text-green-600 bg-green-50 rounded-md">
            {t("auth.passwordResetSuccessMessage")}
          </div>
        ) : (
          <form onSubmit={resetPasswordForm.handleSubmit}>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="password">{t("auth.newPassword")}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={resetPasswordForm.values.password}
                  onChange={resetPasswordForm.handleChange}
                  onBlur={resetPasswordForm.handleBlur}
                  disabled={isLoading}
                />
                {resetPasswordForm.touched.password &&
                  resetPasswordForm.errors.password && (
                    <p className="text-sm text-destructive">
                      {resetPasswordForm.errors.password}
                    </p>
                  )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">
                  {t("auth.confirmNewPassword")}
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={resetPasswordForm.values.confirmPassword}
                  onChange={resetPasswordForm.handleChange}
                  onBlur={resetPasswordForm.handleBlur}
                  disabled={isLoading}
                />
                {resetPasswordForm.touched.confirmPassword &&
                  resetPasswordForm.errors.confirmPassword && (
                    <p className="text-sm text-destructive">
                      {resetPasswordForm.errors.confirmPassword}
                    </p>
                  )}
              </div>

              <Button
                className="w-full mt-2"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? t("common.processing") : t("auth.resetPassword")}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
      <CardFooter className="flex flex-col">
        <div className="mt-2 text-center text-sm">
          {t("auth.rememberPassword")}{" "}
          <a href="/login" className="underline text-primary">
            {t("auth.signIn")}
          </a>
        </div>
      </CardFooter>
    </Card>
  );
}
